import { useId } from 'react';
import { FieldSize, Size, t, usePhoneInput } from '@bms2/shared';
import { SelectChevron } from './Select';
import { TextInput } from './TextInput';
import fieldStyles from './field.module.css';
import styles from './PhoneInput.module.css';

/**
 * Phone Input (piece 3, decision 0340): the one control a phone number is
 * typed into, wherever it is typed. Three parts stand in one row — a country
 * dropdown opening on the United States, the national number, and the
 * extension where the record has a column for one. The value the control
 * holds is the stored E.164 string, so a number reopened in the editor comes
 * back in its parts and never as a string somebody has to re-read. 🔨 The
 * extension is its own field, kept apart from the number for the dialer, and
 * a caller with no column for it draws no field for it.
 */
export interface PhoneInputProps {
  label: string;
  /** The stored value, E.164 or ''; a legacy value with no plus is read as a national number. */
  value: string;
  onChange: (stored: string) => void;
  /** The extension where the record carries one; absent, no extension field is drawn. */
  extension?: string | null;
  onExtensionChange?: (extension: string) => void;
  required?: boolean;
  disabled?: boolean;
  /** A message the page decided, such as a server's answer. */
  error?: string | null;
  /** The national number's input name, which is what a form reads. */
  name?: string;
  extensionName?: string;
  /** The country the empty control opens on; the United States unless the record says otherwise. */
  defaultCountry?: string;
  onBlur?: () => void;
  /** Open already marked, as a field re-rendered after a submit attempt: the message shows on load. */
  initiallyMarked?: boolean;
  /**
   * Which of the widths the national number's box takes, Column in a line table's cell (decisions 0583, 0647).
   * The country select sizes itself to its own text and the extension keeps its
   * six characters, so a phone row is the number's size plus those two.
   */
  size?: FieldSize;
}

export const phoneInputDocumentedStates = ['default', 'withExtension', 'international', 'other', 'error', 'disabled'] as const;

export function PhoneInput({ label, value, onChange, extension, onExtensionChange, required = false, disabled = false, error = null, name, extensionName, defaultCountry, onBlur, initiallyMarked = false, size = Size.Medium }: PhoneInputProps) {
  const phone = usePhoneInput(defaultCountry === undefined ? { value, onChange } : { value, onChange, defaultCountry });
  const legendId = useId();
  const withExtension = extension !== undefined;
  const numberLabel = `${label} ${t('phone.number').toLowerCase()}`;
  const countryLabel = `${label} ${t('phone.country').toLowerCase()}`;
  return (
    <fieldset className={styles.group} aria-labelledby={legendId} disabled={disabled} data-phone-input data-phone-country={phone.country} data-phone-stored={value}>
      <legend id={legendId} className={fieldStyles.label}>
        {label}
        {required ? (
          <span className={fieldStyles.required} aria-hidden="true">
            {t('field.requiredMark')}
          </span>
        ) : null}
      </legend>
      <div className={styles.row}>
        {/*
          The country dropdown is the native select: a closed list with no search, and the phone's own picker below md. Its closed box is the Dropdown's (decision 1450).
          🔒 It stands at the Small width and its closed face reads the code and the dial code, "US +1"; the open list keeps each country's name (decision 1451).
        */}
        <span className={`${fieldStyles.selectBox} ${styles.country}`}>
          <span className={styles.countryFace} aria-hidden="true" data-phone-country-face="true">
            {(phone.options.find((option) => option.code === phone.country) ?? phone.options[0])?.short}
          </span>
          <select
            className={fieldStyles.input}
            aria-label={countryLabel}
            value={phone.country}
            disabled={disabled}
            data-phone-field="country"
            onChange={(event) => phone.setCountry(event.target.value)}
          >
            {phone.options.map((option) => (
              <option key={option.code || 'other'} value={option.code}>
                {option.label}
              </option>
            ))}
          </select>
          <SelectChevron />
        </span>
        <div className={styles.number}>
          {phone.mask ? (
            <TextInput
              label={numberLabel}
              labelHidden
              mask={phone.mask}
              value={phone.national}
              onChange={phone.typeNational}
              onBlur={() => {
                phone.settle();
                onBlur?.();
              }}
              validate={phone.validate}
              required={required}
              disabled={disabled}
              error={error}
              initiallyMarked={initiallyMarked}
              size={size}
              {...(name ? { name } : {})}
            />
          ) : (
            <TextInput
              label={numberLabel}
              labelHidden
              type="tel"
              value={phone.national}
              onChange={phone.typeNational}
              onBlur={() => {
                phone.settle();
                onBlur?.();
              }}
              validate={phone.validate}
              required={required}
              disabled={disabled}
              error={error}
              initiallyMarked={initiallyMarked}
              size={size}
              {...(name ? { name } : {})}
            />
          )}
        </div>
        {withExtension ? (
          <div className={styles.extension}>
            <TextInput
              label={t('phone.extension')}
              labelHidden
              value={extension ?? ''}
              onChange={(next) => onExtensionChange?.(next.replace(/\D/g, ''))}
              placeholder={t('phone.extensionPlaceholder')}
              disabled={disabled}
              size={Size.Small}
              {...(extensionName ? { name: extensionName } : {})}
            />
          </div>
        ) : null}
      </div>
    </fieldset>
  );
}
